// ============================================================================
// checklistParentRules — the single source of truth for "can this checklist
// live under this parent given this date window".
//
// Composes canParentHostRecurring + isLegacyRecurringContainerId (already the
// enforced rules for recurring parents) and adds the containment / cadence
// rules that were previously documented in comments but not enforced:
//   • window ⊆ parent window (for both INDIVIDUAL under a dated parent and
//     DAILY / WEEKLY once the recurring parent check passes)
//   • DAILY  → start === end (single day)
//   • WEEKLY → a single ISO week, Monday…Sunday
//
// Every rejection can carry a `clampTo` suggestion so UIs can offer a
// one-click "clamp checklist window to fit" fix instead of dumping the user
// into manual date editing.
// ============================================================================

import type { TimelineElement } from '../types/timelineElement'
import {
  canParentHostRecurring,
  isLegacyRecurringContainerId,
} from './recurringParentEligibility'

export type ChecklistParentType = 'INDIVIDUAL' | 'DAILY' | 'WEEKLY'

export type ChecklistWindow = { start: string; end: string }

export type ParentEligibility =
  | { ok: true }
  | { ok: false; reason: string; clampTo?: ChecklistWindow }

export type EvaluateChecklistParentInput = {
  checklistType: ChecklistParentType
  window: ChecklistWindow | null
  parent: TimelineElement | null
  parentId: string | null
  workspaceDefaultContainerId: string
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const isoDate = (value: string): string => value.slice(0, 10)

const isValidIso = (value: unknown): value is string =>
  typeof value === 'string' && ISO_DATE_RE.test(value.slice(0, 10))

const parseIso = (iso: string): Date => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

const formatIso = (date: Date): string => {
  const y = date.getFullYear().toString().padStart(4, '0')
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Monday of the ISO week containing `iso`. */
export const isoWeekMonday = (iso: string): string => {
  const d = parseIso(iso)
  const delta = (d.getDay() + 6) % 7
  d.setDate(d.getDate() - delta)
  return formatIso(d)
}

/** Sunday of the ISO week containing `iso`. */
export const isoWeekSunday = (iso: string): string => {
  const monday = parseIso(isoWeekMonday(iso))
  monday.setDate(monday.getDate() + 6)
  return formatIso(monday)
}

const minIso = (a: string, b: string) => (a <= b ? a : b)
const maxIso = (a: string, b: string) => (a >= b ? a : b)

/**
 * Determine whether the given (checklistType, window, parent) triple is
 * allowed. Callers pass the resolved `parentId` alongside the resolved
 * element (or `null` for workspace-default) so we can detect legacy
 * synthetic parents even when the element lookup returned nothing.
 */
export const evaluateChecklistParent = (
  input: EvaluateChecklistParentInput,
): ParentEligibility => {
  const { checklistType, window, parent, parentId, workspaceDefaultContainerId } = input

  if (parentId && isLegacyRecurringContainerId(parentId)) {
    return { ok: false, reason: 'Top-level recurring canvases are retired. Pick a Task, Goal, or Subtask.' }
  }

  const isWorkspaceDefault =
    !parentId || parentId === workspaceDefaultContainerId || parent === null

  const normalisedWindow: ChecklistWindow | null = window
    ? { start: isoDate(window.start), end: isoDate(window.end) }
    : null

  if (normalisedWindow && normalisedWindow.end < normalisedWindow.start) {
    return {
      ok: false,
      reason: 'Checklist end date is before its start date.',
      clampTo: { start: normalisedWindow.start, end: normalisedWindow.start },
    }
  }

  // Book-type cadence rules first — they don't depend on the parent.
  if (checklistType === 'DAILY' && normalisedWindow) {
    if (normalisedWindow.start !== normalisedWindow.end) {
      return {
        ok: false,
        reason: 'Daily checklists must span a single day.',
        clampTo: { start: normalisedWindow.start, end: normalisedWindow.start },
      }
    }
  }
  if (checklistType === 'WEEKLY' && normalisedWindow) {
    const monday = isoWeekMonday(normalisedWindow.start)
    const sunday = isoWeekSunday(normalisedWindow.start)
    if (normalisedWindow.start !== monday || normalisedWindow.end !== sunday) {
      return {
        ok: false,
        reason: 'Weekly checklists must span a single Monday–Sunday week.',
        clampTo: { start: monday, end: sunday },
      }
    }
  }

  // Parent-kind rules.
  if (checklistType === 'DAILY' || checklistType === 'WEEKLY') {
    if (isWorkspaceDefault) {
      return {
        ok: false,
        reason: 'Recurring checklists must live inside a Task, Goal, or Subtask.',
      }
    }
    const recurringVerdict = canParentHostRecurring(parent)
    if (!recurringVerdict.ok) return { ok: false, reason: recurringVerdict.reason }
  }

  // Window ⊆ parent window (only when both sides are dated).
  if (!isWorkspaceDefault && parent && isValidIso(parent.startDate) && isValidIso(parent.endDate) && normalisedWindow) {
    const parentStart = isoDate(parent.startDate as string)
    const parentEnd = isoDate(parent.endDate as string)
    if (parentEnd < parentStart) {
      return { ok: false, reason: "Parent's end date is before its start date." }
    }
    const outside =
      normalisedWindow.start < parentStart || normalisedWindow.end > parentEnd
    if (outside) {
      const clampedStart = maxIso(normalisedWindow.start, parentStart)
      const clampedEnd = minIso(normalisedWindow.end, parentEnd)
      // If the clamp collapses to an empty range there's no rescue.
      if (clampedEnd < clampedStart) {
        return {
          ok: false,
          reason: `Parent's window (${parentStart} → ${parentEnd}) doesn't overlap this checklist.`,
        }
      }
      // For WEEKLY, the clamp must also respect Monday–Sunday. If clamping
      // breaks the cadence we drop the suggestion rather than propose a
      // window we would immediately reject on the next pass.
      let clamp: ChecklistWindow | undefined = { start: clampedStart, end: clampedEnd }
      if (checklistType === 'WEEKLY') {
        const m = isoWeekMonday(clampedStart)
        const s = isoWeekSunday(clampedStart)
        clamp = m >= parentStart && s <= parentEnd ? { start: m, end: s } : undefined
      } else if (checklistType === 'DAILY') {
        clamp = { start: clampedStart, end: clampedStart }
      }
      return {
        ok: false,
        reason: `Parent's window is ${parentStart} → ${parentEnd}. Checklist must sit inside it.`,
        clampTo: clamp,
      }
    }
  }

  return { ok: true }
}

export type CandidateVerdict = {
  element: TimelineElement | null // null = workspace-default option
  verdict: ParentEligibility
}

/**
 * Annotate every candidate with its verdict for the given checklist window /
 * type. Used by the parent-picker popover so it can render eligible entries
 * enabled and ineligible ones disabled with `verdict.reason` as tooltip.
 */
export const listEligibleParents = (input: {
  candidates: TimelineElement[]
  checklistType: ChecklistParentType
  window: ChecklistWindow | null
  workspaceDefaultContainerId: string
  includeWorkspaceDefault?: boolean
}): CandidateVerdict[] => {
  const {
    candidates,
    checklistType,
    window,
    workspaceDefaultContainerId,
    includeWorkspaceDefault = true,
  } = input

  const rows: CandidateVerdict[] = []
  if (includeWorkspaceDefault) {
    rows.push({
      element: null,
      verdict: evaluateChecklistParent({
        checklistType,
        window,
        parent: null,
        parentId: workspaceDefaultContainerId,
        workspaceDefaultContainerId,
      }),
    })
  }
  for (const element of candidates) {
    rows.push({
      element,
      verdict: evaluateChecklistParent({
        checklistType,
        window,
        parent: element,
        parentId: element.id,
        workspaceDefaultContainerId,
      }),
    })
  }
  return rows
}
